/**
 * Anúncios — campanhas da Meta.
 *
 * Leitura e operação do dia a dia: gasto e resultado do período, e na lista
 * de campanhas as ações de pausar/ativar, editar verba e duplicar. As travas
 * de dinheiro continuam no servidor (tudo nasce pausado, ativar e mexer em
 * verba exigem `ads:activate`, o teto é do workspace) — a tela só mostra o
 * que a permissão permite.
 *
 * Dois cuidados que a tela precisa ter, e que são a causa usual de "esse número
 * não bate com o Gerenciador de Anúncios":
 *
 *  - todo valor vem em CENTAVOS da moeda da CONTA, não do tenant. Formatar com
 *    a moeda errada erra por ~5x numa conta em dólar;
 *  - as datas seguem o fuso da conta de anúncios. O período escolhido aqui é
 *    resolvido lá, então mostramos o fuso ao lado dos números.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import DashboardLayout from "@/components/DashboardLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, Wallet, Eye, MousePointerClick, Target, Clock, Rocket } from "lucide-react";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { AdsKpi } from "@/components/ads/AdsKpi";
import { BoostsCard } from "@/components/ads/BoostsCard";
import { DiagnosticCard } from "@/components/ads/DiagnosticCard";
import { CampaignsTable } from "@/components/ads/CampaignsTable";
import { useContaSelecionada } from "@/components/ads/useContaSelecionada";
import { PERIODOS, moeda, numero, percentual, type Periodo } from "@/lib/meta-ads-format";

function AdsContent() {
  const { can } = usePermissions();
  const [, navigate] = useLocation();
  const [periodo, setPeriodo] = useState<Periodo>("last_7d");
  const { accounts, contaSelecionada, setAccountId, currency, conectado, contaBloqueada } = useContaSelecionada();

  const podeVerCredencial = can("ads:credentials_manage");
  const credential = trpc.metaAds.credential.get.useQuery(undefined, { enabled: podeVerCredencial, retry: false });

  const resumo = trpc.metaAds.insights.get.useQuery(
    { level: "account", datePreset: periodo, adAccountId: contaSelecionada?.accountId, limit: 1 },
    { staleTime: 60_000, enabled: conectado },
  );
  const total = resumo.data?.items?.[0];

  if (!conectado && !accounts.isLoading) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <Megaphone className="mx-auto h-10 w-10 text-muted-foreground" />
        <h2 className="mt-4 text-lg font-semibold">Nenhuma conta de anúncios conectada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Conecte a conta da Meta em <strong>Configurações → Integrações → Meta Ads</strong> para acompanhar o gasto e o
          resultado das suas campanhas aqui.
        </p>
        {podeVerCredencial && credential.data && (
          <p className="mt-2 text-sm text-muted-foreground">
            A credencial está salva, mas nenhuma conta ativa está visível — use “Testar conexão” na tela de integrações, ou
            mostre uma conta que esteja oculta.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold">Anúncios</h1>
          <p className="text-sm text-muted-foreground">Campanhas de tráfego da Meta (Facebook e Instagram).</p>
        </div>

        {can("ads:manage") && (
          <Button onClick={() => navigate("/ads/new")}>
            <Rocket className="mr-2 h-4 w-4" /> Impulsionar post
          </Button>
        )}

        <Select value={contaSelecionada?.accountId ?? ""} onValueChange={(v) => setAccountId(v)}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="Conta de anúncios" />
          </SelectTrigger>
          <SelectContent>
            {(accounts.data ?? []).map((a) => (
              <SelectItem key={a.accountId} value={a.accountId}>
                {a.name ?? a.accountId}
                {a.accountStatus != null && ![1, 9].includes(a.accountStatus) ? " ⚠️" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {can("ads:credentials_manage") && (
          <Link href="/settings?integration=meta_ads" className="text-xs text-muted-foreground hover:underline" title="Ocultar contas que não usa">
            Gerenciar contas
          </Link>
        )}

        <Select value={periodo} onValueChange={(v) => setPeriodo(v as Periodo)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODOS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Conta bloqueada é a causa de erro mais confusa deste módulo: nada
          falha visivelmente até alguém tentar alterar algo. */}
      {contaBloqueada && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          Esta conta de anúncios não está ativa na Meta (status {contaSelecionada?.accountStatus}). As campanhas continuam
          listadas, mas alterações vão ser recusadas até a pendência ser resolvida no Gerenciador de Negócios.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <AdsKpi icon={<Wallet className="h-4 w-4" />} label="Gasto" value={moeda(total?.spendCents, currency)} hint={currency ? `em ${currency}` : undefined} />
        <AdsKpi icon={<Eye className="h-4 w-4" />} label="Impressões" value={numero(total?.impressions)} hint={total?.reach ? `${numero(total.reach)} pessoas alcançadas` : undefined} />
        <AdsKpi icon={<MousePointerClick className="h-4 w-4" />} label="Cliques" value={numero(total?.clicks)} hint={total?.ctr != null ? `CTR ${percentual(total.ctr)}` : undefined} />
        <AdsKpi icon={<Target className="h-4 w-4" />} label="Custo por clique" value={moeda(total?.cpcCents, currency)} hint={total?.cpmCents != null ? `CPM ${moeda(total.cpmCents, currency)}` : undefined} />
      </div>

      {contaSelecionada?.timezoneName && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" /> Os períodos seguem o fuso da conta de anúncios ({contaSelecionada.timezoneName}).
        </p>
      )}

      <DiagnosticCard adAccountId={contaSelecionada?.accountId} currency={currency} />

      <BoostsCard canManage={can("ads:manage")} />

      <CampaignsTable
        adAccountId={contaSelecionada?.accountId}
        currency={currency}
        periodo={periodo}
        canManage={can("ads:manage")}
        canActivate={can("ads:activate")}
        contaBloqueada={contaBloqueada}
      />
    </div>
  );
}

export default function Ads() {
  return (
    <PermissionGuard permission="ads:view">
      <DashboardLayout>
        <AdsContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
